// File system access (§8). THE ONLY MODULE THAT WRITES FILES.
//
// Two backends behind one interface:
//  · FsaFolder — a folder picked with showDirectoryPicker({ mode: 'readwrite' }) (or dropped, or an OPFS
//    directory in tests). Sidecars live next to the photos; exports go to exports/<subfolder>/.
//  · FallbackFolder — files from <input type="file" multiple> (Firefox, Safari, permission denied).
//    Sidecars live in IndexedDB keyed name|size|lastModified; exports download as a zip (M6).
// EVERY write passes WriteGuard.assertWritable first (originals can never be written, renamed or
// deleted; see fsGuard.ts). There is no delete except our own temp sidecar.
import { createStore, get, set, del, keys, type UseStore } from 'idb-keyval';
import { WriteGuard, uniqueName, EXPORTS_DIR, DESIGNS_DIR, EPD_EXT } from './fsGuard';
import { isAcceptedName } from './decode/types';
import { sidecarName, maskName } from '../model/sidecar';

export type PhotoRef = { name: string; size: number; lastModified: number };

export interface PhotoFolder {
  readonly name: string;
  readonly mode: 'fsa' | 'fallback';
  readonly guard: WriteGuard;
  /** Accepted photo files, sorted naturally by name (subfolders are ignored in V1). */
  list(): Promise<File[]>;
  readSidecar(photo: PhotoRef): Promise<string | null>;
  writeSidecar(photo: PhotoRef, text: string): Promise<void>;
  readMask(photo: PhotoRef): Promise<Blob | null>;
  writeMask(photo: PhotoRef, png: Uint8Array): Promise<void>;
  /** Writes exports/<subfolder>/<name>, never overwriting (name_2, name_3, …). Returns the path written. */
  writeExport(subfolder: string, name: string, bytes: Uint8Array): Promise<string>;
  /** Design documents: names of designs/<name>.epd (without the extension). */
  listDesigns(): Promise<string[]>;
  readDesign(name: string): Promise<Uint8Array | null>;
  /** Saves designs/<name>.epd atomically (temp + move where supported); overwrites only that document. */
  writeDesign(name: string, bytes: Uint8Array): Promise<void>;
}

type FileHandle = FileSystemFileHandle & { move?: (name: string) => Promise<void> };
type DirHandle = FileSystemDirectoryHandle & {
  values(): AsyncIterable<FileSystemHandle>;
  queryPermission?(o: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(o: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
};

const naturalSort = (a: File, b: File) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

async function writeHandle(h: FileSystemFileHandle, data: BlobPart) {
  const w = await h.createWritable();
  await w.write(data);
  await w.close();
}

/** Counters for tests and the review (how many writes of each kind happened). */
export const fsStats = { sidecarWrites: 0, exportWrites: 0, maskWrites: 0 };

export class FsaFolder implements PhotoFolder {
  readonly mode = 'fsa' as const;
  readonly guard = new WriteGuard();
  constructor(readonly dir: DirHandle) {}
  get name() {
    return this.dir.name;
  }

  async list(): Promise<File[]> {
    const files: File[] = [];
    for await (const h of this.dir.values()) {
      if (h.kind !== 'file' || !isAcceptedName(h.name)) continue;
      files.push(await (h as FileSystemFileHandle).getFile());
    }
    files.sort(naturalSort);
    this.guard.setSources(files.map((f) => f.name));
    return files;
  }

  private async readText(name: string): Promise<string | null> {
    try {
      return await (await (await this.dir.getFileHandle(name)).getFile()).text();
    } catch {
      return null;
    }
  }

  readSidecar(photo: PhotoRef) {
    return this.readText(sidecarName(photo.name));
  }

  /** Atomic where supported: write a temp name, then move() over the final name (§8). */
  async writeSidecar(photo: PhotoRef, text: string): Promise<void> {
    const final = this.guard.assertWritable(sidecarName(photo.name));
    const tmpName = this.guard.assertWritable(final + '.tmp');
    fsStats.sidecarWrites++;
    const tmp = (await this.dir.getFileHandle(tmpName, { create: true })) as FileHandle;
    await writeHandle(tmp, text);
    if (typeof tmp.move === 'function') {
      try {
        await tmp.move(final);
        return;
      } catch {
        /* move() unsupported for this backend: overwrite the sidecar itself, then drop the temp */
      }
    }
    await writeHandle(await this.dir.getFileHandle(final, { create: true }), text);
    try {
      await this.dir.removeEntry(this.guard.assertDeletable(tmpName));
    } catch {
      /* temp already gone */
    }
  }

  async readMask(photo: PhotoRef): Promise<Blob | null> {
    try {
      return await (await this.dir.getFileHandle(maskName(photo.name))).getFile();
    } catch {
      return null;
    }
  }

  async writeMask(photo: PhotoRef, png: Uint8Array): Promise<void> {
    const name = this.guard.assertWritable(maskName(photo.name));
    fsStats.maskWrites++;
    await writeHandle(await this.dir.getFileHandle(name, { create: true }), png as Uint8Array<ArrayBuffer>);
  }

  async writeExport(subfolder: string, name: string, bytes: Uint8Array): Promise<string> {
    const sub = subfolder.replace(/[\\/]+/g, '_') || 'export';
    // Validate the folder before touching the disk; the name is checked once it is final.
    this.guard.assertExportDir(`${EXPORTS_DIR}/${sub}`);
    const exportsDir = await this.dir.getDirectoryHandle(EXPORTS_DIR, { create: true });
    const subDir = (await exportsDir.getDirectoryHandle(sub, { create: true })) as DirHandle;
    const existing = new Set<string>();
    for await (const h of subDir.values()) existing.add(h.name.toLowerCase());
    // An original's name counts as taken too (D27), so the export is renamed instead of refused (S1).
    const finalName = uniqueName(name, (n) => existing.has(n.toLowerCase()) || this.guard.isSource(n));
    const path = this.guard.assertWritable(`${EXPORTS_DIR}/${sub}/${finalName}`);
    fsStats.exportWrites++;
    await writeHandle(
      await subDir.getFileHandle(finalName, { create: true }),
      bytes as Uint8Array<ArrayBuffer>,
    );
    return path;
  }

  async listDesigns(): Promise<string[]> {
    try {
      const dir = (await this.dir.getDirectoryHandle(DESIGNS_DIR)) as DirHandle;
      const names: string[] = [];
      for await (const h of dir.values())
        if (h.kind === 'file' && h.name.toLowerCase().endsWith(EPD_EXT))
          names.push(h.name.slice(0, -EPD_EXT.length));
      return names.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    } catch {
      return [];
    }
  }

  async readDesign(name: string): Promise<Uint8Array | null> {
    try {
      const dir = await this.dir.getDirectoryHandle(DESIGNS_DIR);
      const f = await (await dir.getFileHandle(name + EPD_EXT)).getFile();
      return new Uint8Array(await f.arrayBuffer());
    } catch {
      return null;
    }
  }

  async writeDesign(name: string, bytes: Uint8Array): Promise<void> {
    const final = this.guard.assertWritable(`${DESIGNS_DIR}/${name}${EPD_EXT}`).split('/')[1]!;
    const tmpName = this.guard.assertWritable(`${DESIGNS_DIR}/${final}.tmp`).split('/')[1]!;
    const dir = await this.dir.getDirectoryHandle(DESIGNS_DIR, { create: true });
    const tmp = (await dir.getFileHandle(tmpName, { create: true })) as FileHandle;
    await writeHandle(tmp, bytes as Uint8Array<ArrayBuffer>);
    if (typeof tmp.move === 'function') {
      try {
        await tmp.move(final);
        return;
      } catch {
        /* move() unsupported: overwrite the document itself, then drop the temp */
      }
    }
    await writeHandle(await dir.getFileHandle(final, { create: true }), bytes as Uint8Array<ArrayBuffer>);
    try {
      await dir.removeEntry(this.guard.assertDeletable(`${DESIGNS_DIR}/${tmpName}`).split('/')[1]!);
    } catch {
      /* temp already gone */
    }
  }
}

// ── Fallback (no folder access) ──────────────────────────────────────
let sidecarDb: UseStore | null = null;
let maskDb: UseStore | null = null;
const sidecars = () => (sidecarDb ??= createStore('epb-sidecars', 'sidecars'));
const masks = () => (maskDb ??= createStore('epb-masks', 'masks'));
const keyOf = (p: PhotoRef) => `${p.name}|${p.size}|${p.lastModified}`;

export type ZipSink = (path: string, bytes: Uint8Array) => void;

export class FallbackFolder implements PhotoFolder {
  readonly mode = 'fallback' as const;
  readonly guard = new WriteGuard();
  /** Exports collect here; the export dialog downloads them as a zip (M6). */
  zipSink: ZipSink | null = null;
  constructor(
    private files: File[],
    readonly name = '',
  ) {
    this.guard.setSources(files.map((f) => f.name));
    // Fallback mode keeps every sidecar in IndexedDB: ask the browser not to evict it under disk
    // pressure (S15). A refusal changes nothing.
    void navigator.storage?.persist?.().catch(() => false);
  }
  async list() {
    return [...this.files].filter((f) => isAcceptedName(f.name)).sort(naturalSort);
  }
  async readSidecar(p: PhotoRef) {
    try {
      return (await get<string>(keyOf(p), sidecars())) ?? null;
    } catch {
      return null;
    }
  }
  async writeSidecar(p: PhotoRef, text: string) {
    this.guard.assertWritable(sidecarName(p.name)); // same rule, even though it lands in IndexedDB
    fsStats.sidecarWrites++;
    await set(keyOf(p), text, sidecars());
  }
  async readMask(p: PhotoRef) {
    try {
      return (await get<Blob>(keyOf(p), masks())) ?? null;
    } catch {
      return null;
    }
  }
  async writeMask(p: PhotoRef, png: Uint8Array) {
    this.guard.assertWritable(maskName(p.name));
    fsStats.maskWrites++;
    await set(keyOf(p), new Blob([png as Uint8Array<ArrayBuffer>], { type: 'image/png' }), masks());
  }
  async writeExport(subfolder: string, name: string, bytes: Uint8Array) {
    const path = this.guard.assertWritable(`${EXPORTS_DIR}/${subfolder || 'export'}/${name}`);
    if (!this.zipSink) throw new Error('fallback export needs a zip sink');
    fsStats.exportWrites++;
    this.zipSink(path, bytes);
    return path;
  }
  async listDesigns() {
    return listDesignsIdb();
  }
  async readDesign(name: string) {
    return readDesignIdb(name);
  }
  async writeDesign(name: string, bytes: Uint8Array) {
    this.guard.assertWritable(`${DESIGNS_DIR}/${name}${EPD_EXT}`);
    await writeDesignIdb(name, bytes);
  }
  async forgetSidecar(p: PhotoRef) {
    await del(keyOf(p), sidecars());
  }
}

// ── Design documents without a folder (or in fallback mode): IndexedDB ──
let designDb: UseStore | null = null;
const designs = () => (designDb ??= createStore('epb-designs', 'designs'));
export async function listDesignsIdb(): Promise<string[]> {
  try {
    return ((await keys(designs())) as string[]).sort();
  } catch {
    return [];
  }
}
export async function readDesignIdb(name: string): Promise<Uint8Array | null> {
  try {
    return (await get<Uint8Array>(name, designs())) ?? null;
  } catch {
    return null;
  }
}
export async function writeDesignIdb(name: string, bytes: Uint8Array): Promise<void> {
  await set(name, bytes, designs());
}

// ── Opening ──────────────────────────────────────────────────────────
export const hasFsa = () =>
  typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';

export async function pickFolder(): Promise<FsaFolder | null> {
  const w = window as unknown as { showDirectoryPicker: (o: object) => Promise<DirHandle> };
  try {
    return new FsaFolder(await w.showDirectoryPicker({ mode: 'readwrite' }));
  } catch (e) {
    if ((e as Error).name === 'AbortError') return null;
    throw e;
  }
}

/** Re-requests read/write permission for a remembered folder (needs a user gesture). */
export async function ensurePermission(dir: DirHandle): Promise<boolean> {
  if (!dir.queryPermission) return true;
  if ((await dir.queryPermission({ mode: 'readwrite' })) === 'granted') return true;
  return (await dir.requestPermission?.({ mode: 'readwrite' })) === 'granted';
}

export function pickFilesFallback(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff';
    input.onchange = () => resolve(Array.from(input.files ?? []));
    input.click();
  });
}

/** A dropped folder (DataTransferItem.getAsFileSystemHandle), or null when the drop was not a folder. */
export async function folderFromDrop(items: DataTransferItemList): Promise<FsaFolder | null> {
  for (const it of Array.from(items)) {
    const getHandle = (it as unknown as { getAsFileSystemHandle?: () => Promise<FileSystemHandle | null> })
      .getAsFileSystemHandle;
    if (!getHandle) continue;
    const h = await getHandle.call(it);
    if (h && h.kind === 'directory') return new FsaFolder(h as DirHandle);
  }
  return null;
}

// ── Recent folders (handles in IndexedDB, §8) ────────────────────────
let recentDb: UseStore | null = null;
const recent = () => (recentDb ??= createStore('epb-recent', 'recent'));
export type RecentFolder = { name: string; openedAt: number; handle: DirHandle };

export async function rememberFolder(f: FsaFolder) {
  try {
    const list = ((await get<RecentFolder[]>('list', recent())) ?? []).filter((r) => r.name !== f.name);
    list.unshift({ name: f.name, openedAt: Date.now(), handle: f.dir });
    await set('list', list.slice(0, 10), recent());
  } catch {
    /* storage unavailable: no recents */
  }
}

export async function recentFolders(): Promise<RecentFolder[]> {
  try {
    return (await get<RecentFolder[]>('list', recent())) ?? [];
  } catch {
    return [];
  }
}

// ── Single "Save as…" (kept from M4) ─────────────────────────────────
export async function saveAs(
  bytes: Uint8Array | Blob,
  suggestedName: string,
  mime: string,
): Promise<'saved' | 'downloaded' | 'cancelled'> {
  const w = window as unknown as {
    showSaveFilePicker?: (o: object) => Promise<{
      createWritable(): Promise<{ write(d: BlobPart): Promise<void>; close(): Promise<void> }>;
    }>;
  };
  const blob = bytes instanceof Blob ? bytes : new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mime });
  if (w.showSaveFilePicker) {
    try {
      const ext = suggestedName.slice(suggestedName.lastIndexOf('.'));
      const h = await w.showSaveFilePicker({
        suggestedName,
        types: [{ description: mime, accept: { [mime]: [ext] } }],
      });
      const ws = await h.createWritable();
      await ws.write(blob);
      await ws.close();
      return 'saved';
    } catch (e) {
      if ((e as Error).name === 'AbortError') return 'cancelled';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}
